import {
  SLICE_OUTPUT_CAP_BYTES,
  SliceOsfResult,
  estimateSliceSizeBytes,
  isSliceOverCap,
} from '@ouster/osf-slice';
import { elapsedFramesToHms, SliceUnit } from '@ouster/webviz-react-components';

import { throttle } from 'lodash';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useChakraHook from './useChakraHook';
import { VizPageRef } from '../containers/VizPage';
import { downloadBlob } from '../utils/downloadBlob';

const SECONDS_PRESETS = [1, 5, 10];
const FRAMES_PRESETS = [10, 20, 30];

/**
 * Rounds a byte count to whole megabytes for the over-cap warning message
 * @param bytes byte count
 * @returns e.g. "512MB"
 */
const formatMB = (bytes: number): string =>
  `${Math.round(bytes / (1024 * 1024))}MB`;

/**
 * Formats a frame index as display text for the given unit
 * @param idx frame index
 * @param unit 'seconds' or 'frames'
 * @param fps replay fps
 * @param startIdx first frame index in the recording (elapsed is
 * relative to this)
 * @returns formatted text
 */
const formatValueText = (
  idx: number,
  unit: SliceUnit,
  fps: number,
  startIdx: number
): string => {
  if (unit === 'frames') {
    return String(idx);
  }
  return elapsedFramesToHms(idx - startIdx, fps);
};

/**
 * Parses user-edited text back into a frame index. Returns null if the
 * text isn't a valid timecode/frame number
 * @param text user-edited text
 * @param unit 'seconds' or 'frames'
 * @param fps replay fps
 * @param startIdx first frame index in the recording
 * @returns parsed frame index, or null if invalid
 */
const parseValueText = (
  text: string,
  unit: SliceUnit,
  fps: number,
  startIdx: number
): number | null => {
  if (unit === 'frames') {
    const parsed = parseInt(text.trim(), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  const parts = text
    .trim()
    .split(':')
    .map((part) => Number(part));
  if (parts.length === 0 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }

  let seconds = 0;
  if (parts.length === 3) {
    seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    seconds = parts[0] * 60 + parts[1];
  } else {
    [seconds] = parts;
  }
  return startIdx + Math.round(seconds * fps);
};

/**
 * Compact "<start><unit>_<end><unit>_slice" label for the downloaded
 * file's default name
 * @param unit 'seconds' or 'frames'
 * @param sliceStartIdx first frame index in the slice (inclusive)
 * @param sliceEndIdx last frame index in the slice (inclusive)
 * @param fps replay fps
 * @param startIdx first frame index in the recording
 * @returns filename suffix, without the leading source-name underscore
 */
const formatFilenameSuffix = (
  unit: SliceUnit,
  sliceStartIdx: number,
  sliceEndIdx: number,
  fps: number,
  startIdx: number
): string => {
  if (unit === 'frames') {
    return `${sliceStartIdx}frm_${sliceEndIdx}frm_slice`;
  }
  const startSec = Math.round((sliceStartIdx - startIdx) / fps);
  const endSec = Math.round((sliceEndIdx - startIdx) / fps);
  return `${startSec}s_${endSec}s_slice`;
};

interface WorkerSliceMessage extends MessageEvent {
  data: {
    command?: string;
    requestId?: number;
    result?: SliceOsfResult;
    fraction?: number;
  };
}

/**
 * Posts a `'slice'` command to the (singleton) OSF worker and resolves
 * once the matching `'slice-result'` response arrives, correlated by a
 * locally-incremented requestId (the worker is shared/long-lived, so a
 * plain one-shot listener isn't enough to avoid cross-talk with a
 * hypothetical second in-flight call). Intermediate `'slice-progress'`
 * messages for the same requestId are forwarded to `onProgress` rather
 * than resolving the promise - only the final `'slice-result'` does that.
 * @param worker the shared OSF worker (from getOSFWorker())
 * @param params slice request params
 * @param onProgress called with a rough completion fraction (0-1) as the
 * worker processes chunks, if provided
 * @returns the slice result posted back by the worker
 */
function requestSliceFromWorker(
  worker: Worker,
  params: {
    requestId: number;
    url: string;
    fileSizeBytes: number;
    totalFrames: number;
    sliceStartIdx: number;
    sliceEndIdx: number;
  },
  onProgress?: (fraction: number) => void
): Promise<SliceOsfResult> {
  return new Promise((resolve) => {
    const handleMessage = (e: WorkerSliceMessage) => {
      const { data } = e;
      if (!data || data.requestId !== params.requestId) return;

      if (data.command === 'slice-progress') {
        if (typeof data.fraction === 'number') onProgress?.(data.fraction);
        return;
      }

      if (data.command !== 'slice-result' || !data.result) return;
      worker.removeEventListener('message', handleMessage as EventListener);
      resolve(data.result);
    };
    worker.addEventListener('message', handleMessage as EventListener);
    worker.postMessage({ command: 'slice', ...params });
  });
}

export interface UseSliceOrchestrationParams {
  isSliceModeAvailable: boolean;
  worker: Worker;
  vizPageRef: RefObject<VizPageRef | null>;
  driveSignedUrl: string | undefined;
  fileSizeBytes: number | null;
  numFrames: number;
  frameStartIdx: number;
  currentFrameIdx: number;
  setCurrentFrameIdx: (idx: number) => void;
  lastReceivedFrameIdx: number;
  fps: number;
  setPlaying: (playing: boolean) => void;
  recordingTitle?: string;
  /** Cloud slice output-size cap in bytes, defaults to {@link SLICE_OUTPUT_CAP_BYTES} */
  capBytes?: number;
}

/**
 * Owns slice-mode UI state (range, unit, presets, active handle,
 * thumbnail capture) and the download action for data-app's cloud OSF
 * slicing feature - the DriveViewer equivalent of ouster-viewer's
 * useSliceOrchestration, minus Electron IPC (downloads go through a Blob
 * + <a download> instead of a save-file-path round trip)
 * @param params see UseSliceOrchestrationParams
 * @returns slice mode props for PlayBar and SliceOsfCard
 */
export function useSliceOrchestration({
  isSliceModeAvailable,
  worker,
  vizPageRef,
  driveSignedUrl,
  fileSizeBytes,
  numFrames,
  frameStartIdx,
  currentFrameIdx,
  setCurrentFrameIdx,
  lastReceivedFrameIdx,
  fps,
  setPlaying,
  recordingTitle,
  capBytes = SLICE_OUTPUT_CAP_BYTES,
}: UseSliceOrchestrationParams) {
  const { showToast } = useChakraHook();
  const [isSliceMode, setIsSliceMode] = useState(false);
  const [sliceStartIdx, setSliceStartIdx] = useState(0);
  const [sliceEndIdx, setSliceEndIdx] = useState(0);
  const [unit, setUnit] = useState<SliceUnit>('frames');
  const [activeSliceHandle, setActiveSliceHandle] = useState<'start' | 'end'>(
    'end'
  );
  const [startThumbnailSrc, setStartThumbnailSrc] = useState<string | null>(
    null
  );
  const [endThumbnailSrc, setEndThumbnailSrc] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sliceProgressFraction, setSliceProgressFraction] = useState<
    number | null
  >(null);

  // Base name only (no extension - the consumer always appends its own
  // and never shows/edits it), following the same "<source>_<suffix>"
  // convention the old rename prompt's default value used
  const defaultFileName = useMemo(() => {
    const baseName = recordingTitle || 'recording';
    const suffix = formatFilenameSuffix(
      unit,
      sliceStartIdx,
      sliceEndIdx,
      fps,
      frameStartIdx
    );
    return `${baseName}_${suffix}`;
  }, [recordingTitle, unit, sliceStartIdx, sliceEndIdx, fps, frameStartIdx]);

  // null (rather than '') distinguishes "user hasn't touched this field
  // yet" from "user cleared it while typing" - null follows the default
  // naming convention as the slice range/unit changes, right up until the
  // user types their own name, at which point their edit wins and is
  // never silently overwritten (see onToggleSliceMode's reset for how a
  // *new* slice session gets back to the default)
  const [fileNameOverride, setFileNameOverride] = useState<string | null>(null);
  const fileName = fileNameOverride ?? defaultFileName;

  // Every keystroke overwrites the override, even one that clears the
  // field to blank - reverting immediately here would fight the user
  // while they're mid-edit (e.g. selecting-all then typing a replacement
  // necessarily passes through an empty value). The blank-becomes-
  // default fallback only applies once they're done editing - see
  // onFileNameBlur.
  const onFileNameChange = useCallback((text: string) => {
    setFileNameOverride(text);
  }, []);

  const onFileNameBlur = useCallback(() => {
    if (fileNameOverride !== null && fileNameOverride.trim() === '') {
      setFileNameOverride(null);
    }
  }, [fileNameOverride]);

  // numFrames defaults to Infinity in the drive store until the worker's
  // real framesCount reply arrives - Number.isFinite guards against that
  // placeholder being treated as a loaded, unbounded-high frame count
  const maxIdx =
    Number.isFinite(numFrames) && numFrames > 0 ? numFrames - 1 : frameStartIdx;

  // A serial queue of pending seek-then-capture steps, since a preset
  // click needs to capture both endpoints in sequence and each capture
  // depends on an async seek round-trip through the OSF worker
  const captureQueueRef = useRef<{ idx: number; role: 'start' | 'end' }[]>([]);

  const tryProcessQueue = useCallback(() => {
    const next = captureQueueRef.current[0];
    if (
      !next ||
      currentFrameIdx !== next.idx ||
      lastReceivedFrameIdx !== next.idx
    ) {
      return;
    }
    captureQueueRef.current.shift();
    const dataUrl = vizPageRef.current?.captureFrame() ?? null;
    if (next.role === 'start') {
      setStartThumbnailSrc(dataUrl);
    } else {
      setEndThumbnailSrc(dataUrl);
    }
    const following = captureQueueRef.current[0];
    if (following) {
      setCurrentFrameIdx(following.idx);
    }
  }, [currentFrameIdx, lastReceivedFrameIdx, vizPageRef, setCurrentFrameIdx]);

  useEffect(() => {
    tryProcessQueue();
  }, [tryProcessQueue]);

  const enqueueCapture = useCallback(
    (steps: { idx: number; role: 'start' | 'end' }[]) => {
      const wasEmpty = captureQueueRef.current.length === 0;
      captureQueueRef.current.push(...steps);
      if (wasEmpty && steps.length > 0) {
        setCurrentFrameIdx(steps[0].idx);
        tryProcessQueue();
      }
    },
    [setCurrentFrameIdx, tryProcessQueue]
  );

  const requestIdRef = useRef(0);

  // Fire-and-forget: aborts the in-flight slice's network fetches via the
  // worker's per-requestId AbortController (see worker.ts's 'cancel-slice'
  // handler) - the pending requestSlice promise settles on its own via the
  // 'slice-result' message, reporting `cancelled: true`.
  const onCancelDownload = useCallback(() => {
    worker.postMessage({
      command: 'cancel-slice',
      requestId: requestIdRef.current,
    });
  }, [worker]);

  const requestSlice = useCallback(
    (params: { sliceStartIdx: number; sliceEndIdx: number }) => {
      requestIdRef.current += 1;
      if (!driveSignedUrl || fileSizeBytes === null) {
        return Promise.resolve<SliceOsfResult>({
          ok: false,
          error: 'Recording is not fully loaded yet',
        });
      }
      return requestSliceFromWorker(
        worker,
        {
          requestId: requestIdRef.current,
          url: driveSignedUrl,
          fileSizeBytes,
          totalFrames: numFrames,
          sliceStartIdx: params.sliceStartIdx,
          sliceEndIdx: params.sliceEndIdx,
        },
        setSliceProgressFraction
      );
    },
    [worker, driveSignedUrl, fileSizeBytes, numFrames]
  );

  const onToggleSliceMode = useCallback(() => {
    if (isSliceMode) {
      setIsSliceMode(false);
      return;
    }
    setPlaying(false);
    const defaultDurationFrames =
      unit === 'seconds' ? Math.round(10 * fps) : 10;
    const newEnd = Math.min(currentFrameIdx + defaultDurationFrames, maxIdx);
    setIsSliceMode(true);
    setSliceStartIdx(currentFrameIdx);
    setSliceEndIdx(newEnd);
    setActiveSliceHandle('end');
    // Each newly opened slice session starts with the default filename,
    // even if a previous session in this same tab was renamed by hand
    setFileNameOverride(null);

    // The viewport is already showing curIdx, so the start thumbnail can
    // be captured immediately, without a seek round-trip
    setStartThumbnailSrc(vizPageRef.current?.captureFrame() ?? null);
    if (newEnd === currentFrameIdx) {
      setEndThumbnailSrc(vizPageRef.current?.captureFrame() ?? null);
    } else {
      enqueueCapture([{ idx: newEnd, role: 'end' }]);
    }
  }, [
    isSliceMode,
    setPlaying,
    unit,
    fps,
    currentFrameIdx,
    maxIdx,
    vizPageRef,
    enqueueCapture,
  ]);

  const onClose = useCallback(() => {
    captureQueueRef.current = [];
    setIsSliceMode(false);
  }, []);

  // Live drag: throttle the seek like the main scrubber does, but update
  // local slice state on every tick so the handle position tracks the
  // drag immediately. Doesn't capture thumbnails - only the drag-end
  // handler below does.
  const throttledSeek = useMemo(
    () => throttle((idx: number) => setCurrentFrameIdx(idx), 100),
    [setCurrentFrameIdx]
  );
  useEffect(() => () => throttledSeek.cancel(), [throttledSeek]);

  // Which handle is being dragged is decided once, at drag start (see
  // onSliceRangeChangeStart below) - it can't be re-derived per tick by
  // diffing values against their last-committed value, since a
  // rounded/stepped value can stay unchanged for several update ticks
  // even while that handle is the one moving, which previously caused
  // the active-handle highlight to flicker between start/end mid-drag
  const onSliceRangeChange = useCallback(
    (values: readonly number[]) => {
      const newStart = Math.round(values[0]);
      const newEnd = Math.round(values[1]);
      setSliceStartIdx(newStart);
      setSliceEndIdx(newEnd);
      throttledSeek(activeSliceHandle === 'start' ? newStart : newEnd);
    },
    [activeSliceHandle, throttledSeek]
  );

  const onSliceRangeChangeStart = useCallback((role: 'start' | 'end') => {
    setActiveSliceHandle(role);
  }, []);

  const onSliceRangeChangeEnd = useCallback(
    (values: readonly number[], role: 'start' | 'end') => {
      throttledSeek.cancel();
      const newStart = Math.round(values[0]);
      const newEnd = Math.round(values[1]);
      setSliceStartIdx(newStart);
      setSliceEndIdx(newEnd);
      setActiveSliceHandle(role);
      enqueueCapture([{ idx: role === 'start' ? newStart : newEnd, role }]);
    },
    [throttledSeek, enqueueCapture]
  );

  // Clicking a thumbnail jumps the viewport to that endpoint and marks it
  // active, without touching the slice range or re-capturing (the frame
  // there is unchanged, so the existing thumbnail is still valid)
  const onThumbnailClick = useCallback(
    (role: 'start' | 'end') => {
      const idx = role === 'start' ? sliceStartIdx : sliceEndIdx;
      setCurrentFrameIdx(idx);
      setActiveSliceHandle(role);
    },
    [sliceStartIdx, sliceEndIdx, setCurrentFrameIdx]
  );

  const onUnitChange = useCallback((newUnit: SliceUnit) => {
    setUnit(newUnit);
  }, []);

  const commitEndpointText = useCallback(
    (text: string, role: 'start' | 'end') => {
      const parsed = parseValueText(text, unit, fps, frameStartIdx);
      if (parsed === null) return;

      const otherIdx = role === 'start' ? sliceEndIdx : sliceStartIdx;
      const clamped =
        role === 'start'
          ? Math.min(Math.max(parsed, frameStartIdx), otherIdx)
          : Math.max(Math.min(parsed, maxIdx), otherIdx);

      if (role === 'start') {
        setSliceStartIdx(clamped);
      } else {
        setSliceEndIdx(clamped);
      }
      setActiveSliceHandle(role);
      enqueueCapture([{ idx: clamped, role }]);
    },
    [
      unit,
      fps,
      frameStartIdx,
      sliceEndIdx,
      sliceStartIdx,
      maxIdx,
      enqueueCapture,
    ]
  );

  const onStartValueChange = useCallback(
    (text: string) => commitEndpointText(text, 'start'),
    [commitEndpointText]
  );
  const onEndValueChange = useCallback(
    (text: string) => commitEndpointText(text, 'end'),
    [commitEndpointText]
  );

  const onPresetClick = useCallback(
    (value: number) => {
      const durationFrames =
        unit === 'seconds' ? Math.round(value * fps) : value;
      const newEnd = Math.min(sliceStartIdx + durationFrames, maxIdx);
      setSliceEndIdx(newEnd);
      setActiveSliceHandle('end');
      enqueueCapture([{ idx: newEnd, role: 'end' }]);
    },
    [unit, sliceStartIdx, fps, maxIdx, enqueueCapture]
  );

  const sizeEstimateBytes = useMemo(
    () =>
      estimateSliceSizeBytes(
        fileSizeBytes,
        numFrames,
        sliceStartIdx,
        sliceEndIdx
      ),
    [fileSizeBytes, numFrames, sliceStartIdx, sliceEndIdx]
  );
  const warningMessage = isSliceOverCap(sizeEstimateBytes, capBytes)
    ? `This slice is about ${
        sizeEstimateBytes !== null
          ? formatMB(sizeEstimateBytes)
          : 'unknown size'
      }, which exceeds the ${formatMB(
        capBytes
      )} limit for cloud recordings. Choose a shorter range.`
    : null;

  const onDownload = useCallback(async () => {
    if (warningMessage) return;
    const filename = `${fileName || defaultFileName}.osf`;

    setIsDownloading(true);
    setSliceProgressFraction(0);
    try {
      const result = await requestSlice({ sliceStartIdx, sliceEndIdx });
      if (!result.ok) {
        if (!result.cancelled) {
          showToast({
            title: 'Failed to slice recording',
            description: result.error,
            status: 'error',
          });
        }
        return;
      }
      downloadBlob(result.bytes, filename);
      showToast({
        title: 'Slice ready',
        description: filename,
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to slice recording',
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
      });
    } finally {
      setIsDownloading(false);
      setSliceProgressFraction(null);
    }
  }, [
    warningMessage,
    fileName,
    defaultFileName,
    requestSlice,
    sliceStartIdx,
    sliceEndIdx,
    showToast,
  ]);

  const sliceDurationFrames = sliceEndIdx - sliceStartIdx;
  const sliceDurationLabel =
    unit === 'seconds'
      ? `${(sliceDurationFrames / fps).toFixed(1)}s`
      : `${sliceDurationFrames} frames`;

  return {
    isSliceModeAvailable,
    isSliceMode,
    onToggleSliceMode,
    sliceStartIdx,
    sliceEndIdx,
    activeSliceHandle,
    onSliceRangeChangeStart,
    onSliceRangeChange,
    onSliceRangeChangeEnd,
    isSliceActionsLocked: isDownloading,
    unit,
    onUnitChange,
    startValueText: formatValueText(sliceStartIdx, unit, fps, frameStartIdx),
    endValueText: formatValueText(sliceEndIdx, unit, fps, frameStartIdx),
    onStartValueChange,
    onEndValueChange,
    startThumbnailSrc,
    endThumbnailSrc,
    onThumbnailClick,
    durationPresets: unit === 'seconds' ? SECONDS_PRESETS : FRAMES_PRESETS,
    onPresetClick,
    sliceDurationLabel,
    sizeEstimateBytes,
    fileName,
    onFileNameChange,
    onFileNameBlur,
    onDownload,
    isDownloading,
    sliceProgressFraction,
    onCancelDownload,
    warningMessage,
    onClose,
  };
}
